import { useMutation, useSuspenseQuery } from '@apollo/client';
import upperFirst from 'lodash/upperFirst';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';

import { SubmittablePromptSchema } from '@eluve/api-contract';
import { eluveAdminHasuraContext } from '@eluve/apollo-client';
import {
  Badge,
  Button,
  ColDefBuilder,
  DataTable,
  Grid,
  Heading,
  Icon,
  Input,
  Label,
  LinkButton,
  SimpleTooltip,
  Stack,
  Switch,
  Text,
  useToast,
} from '@eluve/components';
import {
  dynamicArtifactTemplateSchema,
  hydrateDynamicArtifactTemplatePreview,
} from '@eluve/llm-outputs';
import { DynamicArtifactRenderer } from '@eluve/smart-blocks';

import { AddOrganizationsToBundleDialog } from './AddOrganizationsToBundleDialog';
import { BundleActiveToggle } from './BundleActiveToggle';
import { DisableBundleConfirmationDialog } from './DisableBundleConfirmationDialog';
import {
  getTemplateBundleByIdQuery,
  getTemplateBundleDisplayName,
  updateTemplateBundleDisplayNameMutation,
  updateTemplateBundleIsActiveMutation,
  updateTemplateBundleIsDefaultForNewTenantsMutation,
} from './template-bundle.operations';
import { useDisableBundleFlow } from './useDisableBundleFlow';

type TenantUsageRow = {
  tenantId: string;
  tenantName: string;
  isEnabled: boolean;
};

const tenantColumns = new ColDefBuilder<TenantUsageRow>()
  .linkSortable(
    'tenantName',
    (row) => `/admin/tenant/${row.tenantId}/manage/template-bundles`,
    'Organization',
  )
  .defaultBoolean('isEnabled', 'Enabled')
  .build();

export const TemplateBundleDetailPage: React.FC = () => {
  const { bundleId } = useParams() as { bundleId: string };
  const toast = useToast();
  const navigate = useNavigate();

  const {
    data: { llmModelPromptTemplates: [bundle] = [] },
  } = useSuspenseQuery(getTemplateBundleByIdQuery, {
    variables: { id: bundleId },
    context: eluveAdminHasuraContext,
  });

  const [updateDisplayName, { loading: updating }] = useMutation(
    updateTemplateBundleDisplayNameMutation,
    {
      context: eluveAdminHasuraContext,
      onCompleted: () => toast.success('Template bundle updated'),
      onError: (error) => toast.error(`Failed to update: ${error.message}`),
    },
  );

  const [toggleActive] = useMutation(updateTemplateBundleIsActiveMutation, {
    context: eluveAdminHasuraContext,
    onError: (error) => toast.error(`Failed to update: ${error.message}`),
  });

  const [toggleDefaultForNewTenants] = useMutation(
    updateTemplateBundleIsDefaultForNewTenantsMutation,
    {
      context: eluveAdminHasuraContext,
      onError: (error) => toast.error(`Failed to update: ${error.message}`),
    },
  );

  const { requestDisable, dialogProps } = useDisableBundleFlow({
    mode: 'global',
  });

  const [isEditingName, setIsEditingName] = useState(false);
  const [editDisplayName, setEditDisplayName] = useState('');

  const handleToggleActive = useCallback(
    async (checked: boolean) => {
      if (checked) {
        toggleActive({ variables: { id: bundleId, isActive: true } });
        return;
      }
      if (!bundle) return;
      requestDisable(bundleId, getTemplateBundleDisplayName(bundle), {
        wasDefaultForNewTenants: bundle.isDefaultForNewTenants ?? false,
      });
    },
    [bundleId, bundle, toggleActive, requestDisable],
  );

  const handleToggleDefaultForNewTenants = useCallback(
    (checked: boolean) => {
      toggleDefaultForNewTenants({
        variables: { id: bundleId, isDefaultForNewTenants: checked },
      });
    },
    [bundleId, toggleDefaultForNewTenants],
  );

  const notFoundHandled = useRef(false);
  useEffect(() => {
    if (!bundle && !notFoundHandled.current) {
      notFoundHandled.current = true;
      toast.error('Template bundle not found');
      navigate('/admin/template-bundles', { replace: true });
    }
  }, [bundle, navigate, toast]);

  if (!bundle) {
    return null;
  }

  const promptVariant = bundle.template?.prompt_template_variants?.[0];
  const outputVariant = bundle.output_template?.variants?.[0];
  const promptName = bundle.template?.name ?? 'Unknown Prompt';
  const modelName =
    bundle.model_args?.name ?? bundle.model_args?.modelType ?? 'Unknown Model';
  const outputName = bundle.output_template?.name ?? 'None';
  const displayName = getTemplateBundleDisplayName(bundle);
  // Tenant-owned bundles are private to one org and can't be a default for
  // new tenants (the DB enforces this too) — see the global-scope guard.
  const isTenantOwned = Boolean(bundle.output_template?.tenantId);

  const startEditingName = () => {
    setEditDisplayName(displayName);
    setIsEditingName(true);
  };

  const cancelEditingName = () => {
    setIsEditingName(false);
  };

  const saveName = async () => {
    const { errors } = await updateDisplayName({
      variables: {
        id: bundleId,
        displayName: editDisplayName,
      },
    });
    if (!errors) {
      setIsEditingName(false);
    }
  };

  // Parse prompt template variant for display
  let promptParts: { role: string; content: string }[] = [];
  if (promptVariant?.template) {
    try {
      promptParts = SubmittablePromptSchema.parse(promptVariant.template);
    } catch {
      // fallback handled below
    }
  }

  // Parse and hydrate output template variant for block rendering
  const hydratedOutputTemplate = (() => {
    if (!outputVariant?.template) return null;
    try {
      const parsed = dynamicArtifactTemplateSchema.parse(
        outputVariant.template,
      );
      return hydrateDynamicArtifactTemplatePreview(parsed);
    } catch {
      return null;
    }
  })();

  const tenantRows: TenantUsageRow[] = (
    bundle.tenant_template_bundles ?? []
  ).map((ttb) => ({
    tenantId: ttb.tenantId,
    tenantName: ttb.tenant?.name ?? ttb.tenantId,
    isEnabled: ttb.isEnabled,
  }));

  return (
    <Stack gap={6}>
      <Stack gap={2}>
        {isEditingName ? (
          <Stack gap={2} align="end" direction="horizontal">
            <Stack gap={1} className="flex-1">
              <Label htmlFor="editDisplayName">Display Name</Label>
              <Input
                id="editDisplayName"
                value={editDisplayName}
                onChange={(e) => setEditDisplayName(e.target.value)}
                autoFocus
              />
            </Stack>
            <Button
              onClick={saveName}
              disabled={!editDisplayName.trim() || updating}
            >
              {updating ? 'Saving...' : 'Save'}
            </Button>
            <Button variant="outline" onClick={cancelEditingName}>
              Cancel
            </Button>
          </Stack>
        ) : (
          <Stack gap={2} align="center" direction="horizontal">
            <div className="my-2 py-2">
              <Heading>{displayName}</Heading>
            </div>
            <button
              type="button"
              onClick={startEditingName}
              className="text-text-tertiary hover:text-text-primary"
            >
              <Icon name="Pencil" />
            </button>
          </Stack>
        )}
        <Stack gap={4} align="center" direction="horizontal">
          <Stack gap={2} align="center" direction="horizontal">
            <BundleActiveToggle
              id="isActive"
              isActive={bundle.isActive}
              modelArgsActive={bundle.model_args?.isActive ?? true}
              onActivate={() => handleToggleActive(true)}
              onDeactivate={() => handleToggleActive(false)}
            />
            <Label htmlFor="isActive">Active</Label>
          </Stack>
          <Stack gap={2} align="center" direction="horizontal">
            <SimpleTooltip
              label={
                isTenantOwned
                  ? "This bundle belongs to a specific organization, so it can't be a default for new tenants."
                  : undefined
              }
            >
              <span className="inline-flex">
                <Switch
                  id="isDefaultForNewTenants"
                  disabled={!bundle.isActive || isTenantOwned}
                  checked={bundle.isDefaultForNewTenants}
                  onCheckedChange={handleToggleDefaultForNewTenants}
                  size="sm"
                />
              </span>
            </SimpleTooltip>
            <Label htmlFor="isDefaultForNewTenants">
              Default for new tenants
            </Label>
          </Stack>
          {bundle.isDefault && <Badge variant="outline">Default</Badge>}
        </Stack>
      </Stack>

      <Grid columns={1} gap={6} className="md:grid-cols-10">
        {/* Prompt Template - 4/10 */}
        <Stack
          gap={2}
          className="rounded-lg border border-border-secondary bg-bg-secondary p-4 md:col-span-4"
        >
          <Stack
            justify="between"
            align="center"
            gap={2}
            direction="horizontal"
          >
            <Heading size="3xl">Prompt Template</Heading>
            {bundle.promptTemplateId && (
              <LinkButton
                variant="outline"
                size="sm"
                to={`/admin/prompts/${bundle.promptTemplateId}`}
              >
                View
              </LinkButton>
            )}
          </Stack>
          <Text size="md" weight="semibold">
            {promptName}
          </Text>
          {bundle.template?.outputType && (
            <Text size="xs" color="tertiary">
              Output Type: {bundle.template.outputType}
            </Text>
          )}
          {bundle.template?.description && (
            <Text size="xs" color="tertiary">
              {bundle.template.description}
            </Text>
          )}
          {promptParts.length > 0 && (
            <Stack gap={4} align="stretch" className="mt-2">
              <Text size="xs" color="secondary">
                Variant {String(promptVariant?.variantNumber)} (Current Default)
              </Text>
              {promptParts.map((part) => (
                <Stack align="stretch" key={part.role} gap={2}>
                  <Heading size="lg">{`${upperFirst(part.role)} Message`}</Heading>
                  <div className="max-h-96 overflow-y-auto rounded-md border border-border-primary bg-bg-secondary p-5">
                    <pre className="text-sm whitespace-pre-wrap">
                      {part.content}
                    </pre>
                  </div>
                </Stack>
              ))}
            </Stack>
          )}
          {promptVariant?.template && promptParts.length === 0 && (
            <pre className="mt-2 max-h-60 overflow-auto rounded-sm bg-bg-quaternary p-3 text-xs text-text-secondary">
              {typeof promptVariant.template === 'string'
                ? promptVariant.template
                : JSON.stringify(promptVariant.template, null, 2)}
            </pre>
          )}
        </Stack>

        {/* Output Template - 4/10 */}
        <Stack
          gap={2}
          className="rounded-lg border border-border-secondary bg-bg-secondary p-4 md:col-span-4"
        >
          <Stack
            justify="between"
            align="center"
            gap={2}
            direction="horizontal"
          >
            <Heading size="3xl">Output Template</Heading>
            {bundle.outputTemplateId && (
              <LinkButton
                variant="outline"
                size="sm"
                to={`/admin/output-templates/${bundle.outputTemplateId}`}
              >
                View
              </LinkButton>
            )}
          </Stack>
          <Text size="md" weight="semibold">
            {outputName}
          </Text>
          {bundle.output_template?.llmOutputType && (
            <Text size="xs" color="tertiary">
              Output Type: {bundle.output_template.llmOutputType}
            </Text>
          )}
          {bundle.output_template?.description && (
            <Text size="xs" color="tertiary">
              {bundle.output_template.description}
            </Text>
          )}
          {hydratedOutputTemplate && (
            <Stack gap={1} align="stretch" className="mt-2">
              <Text size="xs" color="secondary">
                Variant {String(outputVariant?.variantNumber)} (Current Default)
              </Text>
              <div className="max-h-[800px] overflow-y-auto rounded-lg bg-bg-primary p-4">
                <DynamicArtifactRenderer
                  blocks={hydratedOutputTemplate.blocks ?? []}
                  isReadonly
                />
              </div>
            </Stack>
          )}
          {outputVariant?.template && !hydratedOutputTemplate && (
            <pre className="mt-2 max-h-60 overflow-auto rounded-sm bg-bg-quaternary p-3 text-xs text-text-secondary">
              {typeof outputVariant.template === 'string'
                ? outputVariant.template
                : JSON.stringify(outputVariant.template, null, 2)}
            </pre>
          )}
        </Stack>

        {/* Model - 2/10 */}
        <Stack
          gap={2}
          className="rounded-lg border border-border-secondary bg-bg-secondary p-4 md:col-span-2"
        >
          <Heading size="3xl">Model</Heading>
          <Text size="md" weight="semibold">
            {modelName}
          </Text>
          {bundle.model_args?.modelType && (
            <Text size="xs" color="tertiary">
              Model Type: {bundle.model_args.modelType}
            </Text>
          )}
          {bundle.model_args?.description && (
            <Text size="xs" color="tertiary">
              {bundle.model_args.description}
            </Text>
          )}
          {bundle.model_args?.args && (
            <pre className="mt-2 max-h-40 overflow-auto rounded-sm bg-bg-quaternary p-3 text-xs text-wrap break-all text-text-secondary">
              {JSON.stringify(bundle.model_args.args, null, 2)}
            </pre>
          )}
        </Stack>
      </Grid>

      <Stack gap={2}>
        <Stack direction="horizontal" align="center" justify="between" gap={2}>
          <Heading size="3xl">Tenant Usage</Heading>
          {/* Only active bundles can be enabled for organizations. Enabling an
              inactive bundle would let it participate in default/fallback
              resolution, so hide the action until the bundle is active. */}
          {bundle.isActive && (
            <AddOrganizationsToBundleDialog bundleId={bundleId} />
          )}
        </Stack>
        {tenantRows.length > 0 ? (
          <DataTable
            columns={tenantColumns}
            data={tenantRows}
            isPaginated={false}
          />
        ) : (
          <Text size="sm" color="tertiary">
            No organizations have this bundle enabled yet.
          </Text>
        )}
      </Stack>

      <DisableBundleConfirmationDialog {...dialogProps} />
    </Stack>
  );
};
